import { createFileRoute, Link } from "@tanstack/react-router";
import { ArticleLayout } from "@/components/ArticleLayout";
import { ogImageFor, ogMetaTags, twitterTextTags } from "@/lib/ogImages";
import { getArticleBySlug } from "@/lib/learningHub";

const SITE_URL = "https://coloradowebimpressions.com";
const ARTICLE = getArticleBySlug("/learn/small-business-seo-colorado");
const OG_IMAGE = `${SITE_URL}${ogImageFor({ articleCategory: ARTICLE?.category })}`;

export const Route = createFileRoute("/learn/small-business-seo-colorado")({
  head: () => ({
    meta: [
      { title: "Small Business SEO in Colorado: Where to Start (2026)" },
      { name: "description", content: "Small business SEO in Colorado, in plain English: what to fix first, what it costs, and how a single-location business beats bigger competitors locally." },
      { property: "og:title", content: "Small Business SEO in Colorado: Where to Start (2026)" },
      { property: "og:description", content: "What to fix first, what it costs, and how a single-location Colorado business beats bigger competitors locally." },
      { property: "og:url", content: `${SITE_URL}/learn/small-business-seo-colorado` },
      { property: "og:type", content: "article" },
      ...ogMetaTags(OG_IMAGE),
      ...twitterTextTags({
        title: ARTICLE?.title ?? "Small Business SEO in Colorado",
        description: ARTICLE?.desc ?? "Where a Colorado small business should start with SEO.",
      }),
    ],
    links: [{ rel: "canonical", href: "https://coloradowebimpressions.com/learn/small-business-seo-colorado" }],
  }),
  component: Article,
});

function Article() {
  return (
    <ArticleLayout
      eyebrow="SEO"
      read="11 min read"
      title="Small business SEO in Colorado: where to start"
      intro="Most SEO advice is written for companies with marketing departments. This is for the Colorado business where the owner answers the phone — what actually moves the needle, in the order to do it, without a corporate budget."
      breadcrumbs={[
        { label: "Home", to: "/" },
        { label: "Learning Hub", to: "/learn" },
        { label: "Small business SEO" },
      ]}
      path="/learn/small-business-seo-colorado"
      faqs={[
        { q: "Can a small business really compete with big companies in Google?", a: "Locally, yes. Google favors proximity and relevance for local searches. A single-location Colorado business with a complete Google Business Profile, steady reviews, and real city pages routinely outranks national chains in its own service area." },
        { q: "What's the minimum a small business should spend on SEO?", a: "Real, human-done local SEO starts around $950/month. Below that you're buying software. Our Starter plan is $950/mo and covers Local SEO plus Google Maps — the two things that drive calls for most small businesses." },
        { q: "How long before a small business sees SEO results?", a: "Google Business Profile movement typically shows in 30–60 days. Meaningful ranking and lead changes take 90–180 days. Seasonal trades should start before their busy season, not during it." },
      ]}
    >
      <h2>Start with the map, not the website</h2>
      <p>
        For most Colorado small businesses, the Google Business Profile drives more calls than the
        website does. Before anything else: claim and verify your profile, pick the right
        categories, list every service, add real photos, and start a steady habit of asking happy
        customers for reviews. This is free, and it&rsquo;s where the fastest wins live.
      </p>

      <h2>Then fix the foundation</h2>
      <ul>
        <li><strong>A fast, mobile-first website.</strong> Most of your customers search on a phone. If your site is slow or hard to use with one thumb, rankings and leads both suffer.</li>
        <li><strong>One page per service.</strong> &ldquo;Services&rdquo; as a single bullet list can&rsquo;t rank. &ldquo;Water heater repair&rdquo; as its own page can.</li>
        <li><strong>One page per city you serve.</strong> A Colorado Springs business that also serves Monument, Falcon, and Fountain needs a real page for each — unique content, not find-and-replace.</li>
        <li><strong>Consistent name, address, phone</strong> everywhere your business is listed online.</li>
      </ul>

      <h2>Your advantage over the big guys</h2>
      <p>
        National companies have budget; you have proximity and specificity. Google&rsquo;s local
        results weigh how close and how relevant you are to the searcher. A plumbing company in
        Security-Widefield with 80 real reviews and a page about Security-Widefield will beat a
        national chain&rsquo;s generic Colorado page in that neighborhood almost every time.
        Small isn&rsquo;t the disadvantage in local search — generic is.
      </p>

      <h2>What it costs</h2>
      <p>
        Honest numbers for a single-location Colorado business: Local SEO plus Google Maps
        management runs <Link to="/pricing">$950/month on our Starter plan</Link>. Adding AI
        search optimization and social media brings it to $1,650/month. If you need a website
        first, those run $1,850 to $5,950 depending on size, with a year of hosting included.
        Everything is month to month — no annual contracts.
      </p>

      <h2>What to skip (for now)</h2>
      <p>
        Skip paid ads until the foundation is done — ads on a weak website just buy expensive
        disappointment. Skip &ldquo;national SEO&rdquo; packages; you don&rsquo;t need to rank in
        Ohio. And skip any tool subscription you can&rsquo;t name a use for. Spend on the map,
        the site, and reviews first.
      </p>

      <h2>Get help with this</h2>
      <p><Link to="/affordable-seo-small-business-colorado">Small business SEO services and pricing</Link> explains our scope and the next step if you want this work done for your business.</p>

      <h2>What to do next</h2>
      <p>
        Get a <Link to="/free-audit">free digital marketing audit</Link> and we&rsquo;ll tell you
        exactly where your business stands and what to fix first — or read how we approach{" "}
        <Link to="/colorado-seo">SEO across Colorado</Link> and see full details on our{" "}
        <Link to="/pricing">pricing page</Link>.
      </p>
    </ArticleLayout>
  );
}
